import { createAction } from '@reduxjs/toolkit';

import { EditorEntryPoint } from '@app/hooks/editor';
import {
  DOCUMENT_SETUP__REQUESTER_FIELD_INSERTING_CANCEL,
  DOCUMENT_SETUP__REQUESTER_FIELDS_PAGE_OVER_DELETE,
  DOCUMENT_SETUP_CLEAR_ALL_STATE,
  DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_CHANGE,
  DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_DELETE,
  DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_INSERTED,
  DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_INSERTING,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_CHANGE,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_DELETE,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_INSERTED,
  DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_INSERTING,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_CHANGE,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_DELETE,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_INSERTED,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_INSERTING,
  DOCUMENT_SETUP_SETUP_WITH_DRAFT,
} from '@app/store/actionTypes';
import RequesterCheckboxField from '@app/store/documentSetup/RequesterCheckboxField';
import RequesterCheckboxFieldValues from '@app/store/documentSetup/RequesterCheckboxFieldValues';
import RequesterImageField from '@app/store/documentSetup/RequesterImageField';
import RequesterImageFieldValues from '@app/store/documentSetup/RequesterImageFieldValues';
import RequesterTextField from '@app/store/documentSetup/RequesterTextField';
import RequesterTextFieldValues from '@app/store/documentSetup/RequesterTextFieldValues';
import { Thunk } from '@app/store/types';

export function cancelRequesterFieldInserting() {
  return {
    type: DOCUMENT_SETUP__REQUESTER_FIELD_INSERTING_CANCEL,
  };
}

export const clearDocumentSetupState = createAction(DOCUMENT_SETUP_CLEAR_ALL_STATE);

export function changeRequesterTextField(payload: {
  id: string;
  values: RequesterTextFieldValues;
}) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_CHANGE,
    payload,
  };
}

export function deleteRequesterTextField(payload: { id: string }) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_DELETE,
    payload,
  };
}

export function insertRequesterTextField(payload: RequesterTextField) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_INSERTED,
    payload,
  };
}

export function startInsertingRequesterTextField() {
  return {
    type: DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_INSERTING,
  };
}

export function changeRequesterCheckField(payload: {
  id: string;
  values: RequesterCheckboxFieldValues;
}) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_CHANGE,
    payload,
  };
}

export function deleteRequesterCheckField(payload: { id: string }) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_DELETE,
    payload,
  };
}

export function insertRequesterCheckField(payload: RequesterCheckboxField) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_INSERTED,
    payload,
  };
}

export function startInsertingRequesterCheckField() {
  return {
    type: DOCUMENT_SETUP_REQUESTER_CHECK_FIELD_INSERTING,
  };
}

export function deleteMaxPageOverRequesterFields(payload: { maxPage: number }) {
  return {
    type: DOCUMENT_SETUP__REQUESTER_FIELDS_PAGE_OVER_DELETE,
    payload,
  };
}

export function changeRequesterImageField(payload: {
  id: string;
  values: RequesterImageFieldValues;
}) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_CHANGE,
    payload,
  };
}

export function deleteRequesterImageField(payload: { id: string }) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_DELETE,
    payload,
  };
}

export function insertRequesterImageField(payload: RequesterImageField) {
  return {
    type: DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_INSERTED,
    payload,
  };
}

export function startInsertingRequesterImageField() {
  return {
    type: DOCUMENT_SETUP_REQUESTER_IMAGE_FIELD_INSERTING,
  };
}

export const documentSetupSetupWithDraft = createAction<
  {
    entryPoint: EditorEntryPoint;
  },
  typeof DOCUMENT_SETUP_SETUP_WITH_DRAFT
>(DOCUMENT_SETUP_SETUP_WITH_DRAFT);

export type DocumentSetupAction =
  | ReturnType<typeof cancelRequesterFieldInserting>
  | ReturnType<typeof changeRequesterCheckField>
  | ReturnType<typeof changeRequesterImageField>
  | ReturnType<typeof changeRequesterTextField>
  | ReturnType<typeof clearDocumentSetupState>
  | ReturnType<typeof deleteMaxPageOverRequesterFields>
  | ReturnType<typeof deleteRequesterCheckField>
  | ReturnType<typeof deleteRequesterImageField>
  | ReturnType<typeof deleteRequesterTextField>
  | ReturnType<typeof insertRequesterCheckField>
  | ReturnType<typeof insertRequesterImageField>
  | ReturnType<typeof insertRequesterTextField>
  | ReturnType<typeof startInsertingRequesterCheckField>
  | ReturnType<typeof startInsertingRequesterImageField>
  | ReturnType<typeof startInsertingRequesterTextField>
  | ReturnType<typeof documentSetupSetupWithDraft>;

/**
 * PDF 파일을 교체하여 페이지 수가 변경되고, 현재 PDF 페이지 범위 밖에 필드 정보가 있는경우
 * 해당 필드 정보를 삭제합니다.
 */
export const deletePageOverRequesterFields =
  (payload: { maxPage: number }): Thunk =>
  (dispatch) => {
    dispatch(deleteMaxPageOverRequesterFields(payload));
  };
